// Push notification utilities for Sunnah Fasting App
import { 
  isIOSDevice, 
  isIOSInstalled, 
  isIOSPushSupported, 
  requestIOSNotificationPermission,
  registerServiceWorkerForIOS 
} from './iosCompatibility';

const VAPID_PUBLIC_KEY = 'BBEi59kjTeTrYzSu_G7NuxYRloupgbxrRA2E_g124r5gvJZBmu_VYTbwWXmsVKEU2_bbWEx71TTL_9n3h_plrjA';
const API_BASE_URL = 'https://sunnahfasts.com/api';

// Convert VAPID key for subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported (with iOS compatibility)
export function isPushNotificationSupported() {
  if (isIOSDevice()) {
    return isIOSPushSupported();
  }
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

// Check if currently subscribed to push notifications
export async function isSubscribedToPush() {
  if (!isPushNotificationSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    console.error('Error checking push subscription:', error);
    return false;
  }
}

// Subscribe to push notifications
export async function subscribeToPush() {
  if (!isPushNotificationSupported()) {
    throw new Error('Push notifications not supported');
  }

  try {
    console.log('📱 Starting push subscription process...');
    
    // iOS-specific permission handling
    let permission;
    if (isIOSDevice()) {
      permission = await requestIOSNotificationPermission();
    } else {
      permission = await Notification.requestPermission();
    }
    
    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }

    // Get service worker registration (with iOS optimization)
    let registration;
    if (isIOSDevice()) {
      registration = await registerServiceWorkerForIOS();
    } else {
      registration = await navigator.serviceWorker.ready;
    }
    console.log('📱 Service worker ready');

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Create new subscription
      console.log('📱 Creating new push subscription...');
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
    }

    console.log('📱 Push subscription created:', subscription);

    // Send subscription to server
    const response = await fetch(`${API_BASE_URL}/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subscription,
        metadata: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          calendarSource: localStorage.getItem('calendarSource') || 'morocco'
        }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to save subscription to server');
    }

    console.log('📱 Subscription saved to server');
    return subscription;

  } catch (error) {
    console.error('Error subscribing to push:', error);
    throw error;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  if (!isPushNotificationSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Unsubscribe locally
      await subscription.unsubscribe();
      
      // Remove from server
      await fetch(`${API_BASE_URL}/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subscription })
      });
      
      console.log('📱 Successfully unsubscribed from push notifications');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error unsubscribing from push:', error);
    return false;
  }
}

// Update subscription metadata (e.g., when calendar source changes)
export async function updateSubscriptionMetadata(calendarSource) {
  if (!isPushNotificationSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await fetch(`${API_BASE_URL}/update-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription,
          metadata: {
            calendarSource,
            timestamp: new Date().toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        })
      });
      
      console.log('📱 Subscription metadata updated');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error updating subscription metadata:', error);
    return false;
  }
}

// Test push notification
export async function sendTestNotification() {
  try {
    const response = await fetch(`${API_BASE_URL}/test-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error('Failed to send test notification');
    }

    console.log('📱 Test notification sent');
    return true;
  } catch (error) {
    console.error('Error sending test notification:', error);
    throw error;
  }
}